import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { RiAddLine, RiDeleteBinLine, RiPencilLine } from "@remixicon/react";
import React from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import { AndOrList } from "@/common/components/AndOrList";
import CardContainer from "@/common/components/CardContainer";
import { MotionRule } from "@/common/components/MotionComponents";
import { RuleAdvancedForm } from "@/common/components/Rule/RuleAdvancedForm";
import { RuleSimpleForm } from "@/common/components/Rule/RuleSimpleForm";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import {
  FeatureConfigVariantWithEnvironmentRules,
  UpdateFeatureConfig,
} from "@reflag/shared/featureConfigAPI";
import {
  alwaysTrueFilter,
  COMPANY_ID_CONTEXT_FIELD,
  MAX_ROLLOUT_THRESHOLD,
} from "@reflag/shared/filter";
import { isEmpty } from "@reflag/shared/utils/types";

type FeatureConfigProps = {
  baseName: `variants.${number}`;
  index: number;
  isOnlyConfig: boolean;
  impactsProduction: boolean;
  deleteDisabled?: string;
  onEdit: () => void;
  onDelete: () => void;
  onSetDefault: () => void;
};

export const FeatureConfig = ({
  baseName,
  index,
  isOnlyConfig,
  impactsProduction,
  deleteDisabled,
  onEdit,
  onDelete,
  onSetDefault,
}: FeatureConfigProps) => {
  const { env } = useEnvironment();
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${baseName}.environmentRules.${env.id}.customRules`,
  });

  const {
    variant,
    environmentRules,
  }: FeatureConfigVariantWithEnvironmentRules = useWatch<UpdateFeatureConfig>({
    name: baseName,
  });

  if (!variant) return null;

  const isDefault = environmentRules[env.id]?.default;

  return (
    <CardContainer gap={1} pb={isDefault ? 2 : 1}>
      <Flex align="center" gap={4} justify="space-between" px={2}>
        <HStack overflow="hidden" spacing={2}>
          <Flex align="baseline" gap={2} overflow="hidden">
            <Text color="dimmed">Key</Text>
            <Text fontFamily="code" fontWeight="medium" whiteSpace="nowrap">
              {variant.key}
            </Text>
            {!isEmpty(variant.payload) && (
              <>
                <Text color="dimmed" ml={4}>
                  Payload
                </Text>
                <Text fontFamily="code" isTruncated>
                  {JSON.stringify(variant.payload, null, 2)}
                </Text>
              </>
            )}
          </Flex>
          <Restricted role={impactsProduction ? "publisher" : "contributor"}>
            <IconButton
              aria-label="Edit config"
              color="dimmed"
              icon={<RiPencilLine size={16} />}
              size="xs"
              variant="ghost"
              onClick={onEdit}
            />
          </Restricted>
        </HStack>
        <HStack spacing={2}>
          <Text color="dimmed" fontSize="xs" whiteSpace="nowrap">
            Variant #{index + 1}
          </Text>
          {(!isDefault || isOnlyConfig) && (
            <Restricted role={impactsProduction ? "publisher" : "contributor"}>
              <Tooltip label={deleteDisabled} openDelay={300} hasArrow>
                <IconButton
                  aria-label="Delete config"
                  color="dimmed"
                  icon={<RiDeleteBinLine size={16} />}
                  isDisabled={!!deleteDisabled}
                  size="xs"
                  variant="ghost"
                  onClick={onDelete}
                />
              </Tooltip>
            </Restricted>
          )}
        </HStack>
      </Flex>

      {isDefault ? (
        <Card p={4} w="full">
          <HStack justify="space-between">
            <Text color="dimmed" fontSize="md">
              This is the default value for this environment that will be served
              when no other rules match.
            </Text>
            <Badge borderRadius="full" colorScheme="brand">
              Default
            </Badge>
          </HStack>
        </Card>
      ) : (
        <Restricted role={env.isProduction ? "publisher" : "contributor"}>
          <AndOrList
            conjunction="or"
            conjunctionProps={{ ml: 4, zIndex: 1 }}
            direction="vertical"
            gap={0}
          >
            <Card p={4} w="full">
              <RuleSimpleForm
                baseName={`${baseName}.environmentRules.${env.id}`}
                toolbar={
                  <ButtonGroup
                    justifyContent="flex-end"
                    size="xs"
                    variant="outline"
                  >
                    <Button onClick={onSetDefault}>Set default</Button>
                  </ButtonGroup>
                }
              />
            </Card>
            {fields.map(({ id }, index) => (
              <MotionRule key={id}>
                <Card p={4} w="full">
                  <RuleAdvancedForm
                    baseName={`${baseName}.environmentRules.${env.id}.customRules.${index}`}
                    remove={() => remove(index)}
                  />
                </Card>
              </MotionRule>
            ))}
          </AndOrList>

          <Button
            alignSelf="flex-start"
            leftIcon={<RiAddLine size={16} />}
            size="sm"
            variant="ghostDimmed"
            onClick={() =>
              append({
                partialRolloutThreshold: MAX_ROLLOUT_THRESHOLD,
                partialRolloutContextAttribute: COMPANY_ID_CONTEXT_FIELD,
                filter: alwaysTrueFilter,
              })
            }
          >
            Add rule
          </Button>
        </Restricted>
      )}
    </CardContainer>
  );
};
